import React from 'react';
import { playSound } from '../utils/soundEffects';
import './GameOverlay.css';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal dialog built on native <dialog>.showModal(), which makes the rest of
 * the page inert. Where showModal is unavailable (jsdom), it falls back to the
 * `open` attribute and the Tab handler below keeps focus inside anyway.
 */
const ModalOverlay: React.FC<{
  className: string;
  label: string;
  children: React.ReactNode;
}> = ({ className, label, children }) => {
  const ref = React.useRef<HTMLDialogElement>(null);

  React.useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const previous = document.activeElement as HTMLElement | null;
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    dialog.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    return () => {
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      if (previous && previous.isConnected && typeof previous.focus === 'function') {
        previous.focus();
      }
    };
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDialogElement>) => {
    if (e.key !== 'Tab') return;
    const items = Array.from(e.currentTarget.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (items.length === 0) {
      e.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || !e.currentTarget.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !e.currentTarget.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <dialog
      ref={ref}
      className={className}
      aria-label={label}
      onKeyDown={handleKeyDown}
      onCancel={(e) => e.preventDefault()}
    >
      {children}
    </dialog>
  );
};

interface GameOverlayProps {
  isWin: boolean;
  isGameOver: boolean;
  onReset: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

export const GameOverlay: React.FC<GameOverlayProps> = ({
  isWin,
  isGameOver,
  onReset,
}) => {
  React.useEffect(() => {
    if (isWin) {
      playSound('victory');
    } else if (isGameOver) {
      playSound('gameover');
    }
  }, [isWin, isGameOver]);

  if (!isWin && !isGameOver) return null;

  return (
    <>
      {isWin && (
        <ModalOverlay className="win-overlay" label="Pantalla de victoria">
          <div className="win-animation">¡COPA GANADA!</div>
          <button onClick={onReset} className="reset-button" aria-label="Celebrar y reiniciar el juego">
            CELEBRAR Y REPETIR
          </button>
        </ModalOverlay>
      )}
      {isGameOver && (
        <ModalOverlay className="game-over-overlay" label="Pantalla de derrota">
          <div className="game-over-animation">DERROTA</div>
          <button onClick={onReset} className="reset-button" aria-label="Volver a intentar">
            VOLVER A INTENTAR
          </button>
        </ModalOverlay>
      )}
    </>
  );
};
